import { TinyEmitter } from 'tiny-emitter';
import { autoUpdate, offset, flip, shift, hide, arrow, computePosition } from '@floating-ui/dom';
import isEqual from 'lodash.isequal';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var supportsPassive = false;
if (typeof window !== 'undefined') {
    supportsPassive = false;
    try {
        // @ts-ignore
        window.addEventListener('test', null, Object.defineProperty({}, 'passive', {
            get: function () { supportsPassive = true; },
        }));
    }
    catch (e) { }
}
var isIOS = false;
if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    // @ts-expect-error MSStream is missing in window type
    isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
var LocalEventHandler = /** @class */ (function () {
    function LocalEventHandler() {
        this._listeners = [];
    }
    LocalEventHandler.prototype.on = function (targets, type, handler) {
        this._listeners.push({ targets: targets, type: type, handler: handler });
        targets.forEach(function (t) {
            t.addEventListener(type, handler, supportsPassive ? { passive: true } : undefined);
        });
    };
    LocalEventHandler.prototype.clear = function () {
        this._listeners.forEach(function (_a) {
            var targets = _a.targets, type = _a.type, handler = _a.handler;
            targets.forEach(function (t) { return t.removeEventListener(type, handler); });
        });
        this._listeners = [];
    };
    return LocalEventHandler;
}());
var GlobalEventHandler = /** @class */ (function () {
    function GlobalEventHandler() {
        this._listeners = {
            click: [],
            mousedown: [],
            touchend: [],
        };
        this._started = false;
    }
    GlobalEventHandler.prototype.on = function (eventType, handler) {
        if (!this._listeners[eventType].includes(handler)) {
            this._listeners[eventType].push(handler);
        }
    };
    GlobalEventHandler.prototype.off = function (eventType, handler) {
        var index = this._listeners[eventType].indexOf(handler);
        if (index !== -1) {
            this._listeners[eventType].splice(index, 1);
        }
    };
    GlobalEventHandler.prototype.start = function () {
        var _this = this;
        if (this._started) {
            return;
        }
        this._started = true;
        var emit = function (type) { return function (event) { return _this._listeners[type].forEach(function (fn) { return fn(event); }); }; };
        var passive = supportsPassive ? { passive: true, capture: true } : true;
        if (typeof document !== 'undefined' && typeof window !== 'undefined') {
            if (isIOS) {
                document.addEventListener('touchstart', emit('mousedown'), passive);
                document.addEventListener('touchend', emit('touchend'), passive);
            }
            else {
                window.addEventListener('mousedown', emit('mousedown'), true);
                window.addEventListener('click', emit('click'), true);
            }
        }
    };
    return GlobalEventHandler;
}());
var globalEventHandler = new GlobalEventHandler();
if (typeof window !== 'undefined') {
    globalEventHandler.start();
}

var PLACEMENT;
(function (PLACEMENT) {
    PLACEMENT["LEFT"] = "left";
    PLACEMENT["LEFT_START"] = "left-start";
    PLACEMENT["LEFT_END"] = "left-end";
    PLACEMENT["RIGHT"] = "right";
    PLACEMENT["RIGHT_START"] = "right-start";
    PLACEMENT["RIGHT_END"] = "right-end";
    PLACEMENT["TOP"] = "top";
    PLACEMENT["TOP_START"] = "top-start";
    PLACEMENT["TOP_END"] = "top-end";
    PLACEMENT["BOTTOM"] = "bottom";
    PLACEMENT["BOTTOM_START"] = "bottom-start";
    PLACEMENT["BOTTOM_END"] = "bottom-end";
})(PLACEMENT || (PLACEMENT = {}));

var onClick = function (event) {
    var _a;
    var el = event.currentTarget;
    event.omnicaPopperClose = !el.omnicaPopperCloserTouched;
    event.omnicaPopperCloseAll = !!((_a = el.omnicaPopperCloserModifiers) === null || _a === void 0 ? void 0 : _a.all);
};
var onTouchEnd = (function (event) {
    var _a;
    var el = event.currentTarget;
    var first = el.omnicaPopperCloserTouch;
    delete el.omnicaPopperCloserTouch;
    if (event.changedTouches.length === 1) {
        var touch = event.changedTouches[0];
        event.omnicaPopperClose = (Math.abs(touch.screenY - first.screenY) < 20 &&
            Math.abs(touch.screenX - first.screenX) < 20);
        event.omnicaPopperCloseAll = !!((_a = el.omnicaPopperCloserModifiers) === null || _a === void 0 ? void 0 : _a.all);
    }
});
var onTouchCancel = (function (event) {
    var el = event.currentTarget;
    delete el.omnicaPopperCloserTouch;
});
var onTouchStart = (function (event) {
    if (event.changedTouches.length === 1) {
        var el = event.currentTarget;
        el.omnicaPopperCloserTouch = event.changedTouches[0];
        el.addEventListener('touchend', onTouchEnd);
        el.addEventListener('touchcancel', onTouchCancel);
    }
});
var addCloserListeners = function (el) {
    el.addEventListener('click', onClick);
    el.addEventListener('touchstart', onTouchStart, supportsPassive
        ? { passive: true }
        : false);
};
var removeCloserListeners = function (el) {
    el.removeEventListener('click', onClick);
    el.removeEventListener('touchstart', onTouchStart);
    el.removeEventListener('touchend', onTouchEnd);
    el.removeEventListener('touchcancel', onTouchCancel);
};

var normalizeDelay = function (delay) { return (__assign({ show: 0, hide: 0 }, (typeof delay === 'object' ? delay : {
    show: Number(delay),
    hide: Number(delay),
}))); };
var nextAnimationFrame = function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
    return [2 /*return*/, new Promise(function (resolve) { return requestAnimationFrame(resolve); })];
}); }); };
var renameAttribute = function (el, src, dst) {
    if (el.hasAttribute(src)) {
        el.setAttribute(dst, el.getAttribute(src));
        el.removeAttribute(src);
    }
};
var moveArrow = function (el, placement, strategy, coords) {
    var _a;
    var side = placement.split('-')[0];
    Object.assign(el.style, (_a = {
            position: strategy,
            left: coords.x !== undefined ? "".concat(coords.x, "px") : '',
            top: coords.y !== undefined ? "".concat(coords.y, "px") : '',
            right: '',
            bottom: ''
        },
        _a[{
            left: 'right',
            top: 'bottom',
            right: 'left',
            bottom: 'top',
        }[side]] = '-4px',
        _a));
};
var move = function (el, target, options) { return __awaiter(void 0, void 0, void 0, function () {
    var middleware, arrow$1, _a, placement, strategy, x, y, middlewareData, referenceHidden;
    var _b;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                middleware = [];
                if (options.offsetMainAxis || options.offsetCrossAxis) {
                    middleware.push(offset({
                        mainAxis: Number(options.offsetMainAxis),
                        crossAxis: Number(options.offsetCrossAxis),
                    }));
                }
                if (options.overflow.includes('flip')) {
                    middleware.push(flip({
                        boundary: options.boundary,
                    }));
                }
                if (options.overflow.includes('shift')) {
                    middleware.push(shift({
                        boundary: options.boundary,
                        padding: 12,
                    }));
                }
                if (options.overflow.includes('hide')) {
                    middleware.push(hide({ strategy: 'referenceHidden' }));
                }
                arrow$1 = (_b = el.querySelector('[data-arrow]')) !== null && _b !== void 0 ? _b : null;
                if (arrow$1) {
                    middleware.push(arrow({
                        element: arrow$1,
                        padding: arrow$1.offsetWidth / 3,
                    }));
                }
                return [4 /*yield*/, computePosition(target, el, {
                        middleware: middleware,
                        placement: options.placement,
                        strategy: options.strategy,
                    })];
            case 1:
                _a = _c.sent(), placement = _a.placement, strategy = _a.strategy, x = _a.x, y = _a.y, middlewareData = _a.middlewareData;
                el.style.position = strategy;
                el.style.transform = "translate3d(".concat(Math.round(x), "px,").concat(Math.round(y), "px,0)");
                if (arrow$1 && middlewareData.arrow) {
                    moveArrow(arrow$1, placement, strategy, middlewareData.arrow);
                }
                if (middlewareData.hide) {
                    referenceHidden = middlewareData.hide.referenceHidden;
                    if (referenceHidden) {
                        options.onReferenceHidden();
                    }
                }
                return [2 /*return*/];
        }
    });
}); };
var OmnicaPopper = /** @class */ (function () {
    function OmnicaPopper(el, target, options) {
        this.autoHide = false;
        this.disposeTimeout = null;
        this._targetTriggers = [];
        this._popperTriggers = [];
        this._container = null;
        this._options = {
            placement: 'bottom',
            boundary: 'clippingAncestors',
            offsetMainAxis: 0,
            offsetCrossAxis: 0,
            overflow: [],
            strategy: 'absolute',
        };
        this._shown = false;
        this._showing = false;
        this._hiding = false;
        this._disposed = false;
        this._clicked = false;
        this._touched = false;
        this._stopAutoUpdate = null;
        this._unsubscribe = function () { };
        this._el = el;
        this._target = target;
        this._targetEvents = new LocalEventHandler();
        this._popperEvents = new LocalEventHandler();
        this._emitter = new TinyEmitter();
        var targetTriggers = options.targetTriggers, popperTriggers = options.popperTriggers, autoHide = options.autoHide, container = options.container, delay = options.delay, disposeTimeout = options.disposeTimeout, _options = __rest(options, ["targetTriggers", "popperTriggers", "autoHide", "container", "delay", "disposeTimeout"]);
        this._targetTriggers = targetTriggers !== null && targetTriggers !== void 0 ? targetTriggers : [];
        this._popperTriggers = popperTriggers !== null && popperTriggers !== void 0 ? popperTriggers : [];
        this.autoHide = autoHide !== null && autoHide !== void 0 ? autoHide : false;
        this.disposeTimeout = disposeTimeout !== null && disposeTimeout !== void 0 ? disposeTimeout : null;
        this._container = container !== null && container !== void 0 ? container : null;
        this._delay = normalizeDelay(delay !== null && delay !== void 0 ? delay : 0);
        this.options = _options;
        this._subscribe();
    }
    OmnicaPopper.prototype._subscribe = function () {
        var _this = this;
        var onGlobalClick = function (event) { return _this._onGlobalTap(event); };
        var onGlobalTouch = function (event) { return _this._onGlobalTap(event, true); };
        var onGlobalMousedown = function (event) {
            _this._clicked = _this._contains(event.target);
        };
        globalEventHandler.on('click', onGlobalClick);
        globalEventHandler.on('mousedown', onGlobalMousedown);
        globalEventHandler.on('touchend', onGlobalTouch);
        this._unsubscribe = function () {
            globalEventHandler.off('click', onGlobalClick);
            globalEventHandler.off('mousedown', onGlobalMousedown);
            globalEventHandler.off('touchend', onGlobalTouch);
        };
    };
    Object.defineProperty(OmnicaPopper.prototype, "target", {
        get: function () {
            return this._target;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(OmnicaPopper.prototype, "container", {
        get: function () {
            return this._container;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(OmnicaPopper.prototype, "delay", {
        get: function () {
            return this._delay;
        },
        set: function (delay) {
            this._delay = normalizeDelay(delay);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(OmnicaPopper.prototype, "options", {
        get: function () {
            return this._options;
        },
        set: function (options) {
            var offsetMainAxis = options.offsetMainAxis, offsetCrossAxis = options.offsetCrossAxis, _options = __rest(options, ["offsetMainAxis", "offsetCrossAxis"]);
            this._options = __assign(__assign(__assign({}, this._options), _options), { offsetMainAxis: Number(offsetMainAxis), offsetCrossAxis: Number(offsetCrossAxis) });
        },
        enumerable: false,
        configurable: true
    });
    OmnicaPopper.prototype.on = function (event, handler) {
        this._emitter.on(event, handler);
    };
    OmnicaPopper.prototype._emit = function (event, payload) {
        this._emitter.emit(event, payload);
    };
    OmnicaPopper.prototype.initialize = function (disposed) {
        if (disposed === void 0) { disposed = false; }
        this.detach();
        if (!disposed) {
            this._disposed = false;
            var target = this._target();
            if (target) {
                renameAttribute(target, 'title', 'data-original-title');
            }
            this._startTargetListening();
            this._startPopperListening();
        }
        else {
            this._disposed = true;
        }
    };
    OmnicaPopper.prototype.activate = function (show) {
        if (show === void 0) { show = false; }
        if (this._disposed) {
            this.initialize();
            if (show) {
                this.show();
            }
        }
    };
    OmnicaPopper.prototype.show = function (immediately) {
        var _this = this;
        if (immediately === void 0) { immediately = false; }
        if (this._disposed) {
            return;
        }
        this._hiding = false;
        this._schedule(function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        clearTimeout(this._disposeTimer);
                        clearTimeout(this._scheduleTimer);
                        if (this._shown) {
                            return [2 /*return*/];
                        }
                        this._teleport();
                        return [4 /*yield*/, nextAnimationFrame()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, nextAnimationFrame()];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.adjust()];
                    case 3:
                        _a.sent();
                        this.autoUpdateOn();
                        if (!this._hiding) {
                            this._shown = true;
                            this._emit('update:shown', true);
                            /** Смена видимости плавающего элемента на отображение на форме */
                            this._emit('shown');
                        }
                        return [2 /*return*/];
                }
            });
        }); }, immediately ? 0 : this._delay.show);
        /** Появление плавающего элемента */
        this._emit('show');
        this._showing = true;
        requestAnimationFrame(function () { return setTimeout(function () { return _this._showing = false; }); });
    };
    OmnicaPopper.prototype.hide = function (immediately) {
        var _this = this;
        if (immediately === void 0) { immediately = false; }
        this._hiding = true;
        this._schedule(function () { return __awaiter(_this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                clearTimeout(this._scheduleTimer);
                if (!this._shown) {
                    return [2 /*return*/];
                }
                this.autoUpdateOff();
                this._shown = false;
                this._emit('update:shown', false);
                clearTimeout(this._disposeTimer);
                if (this.disposeTimeout !== null) {
                    this._disposeTimer = setTimeout(function () {
                        _this.detach();
                    }, Number(this.disposeTimeout));
                }
                /** Смена видимости плавающего элемента на сокрытие */
                this._emit('hidden');
                return [2 /*return*/];
            });
        }); }, immediately ? 0 : this._delay.hide);
        /** Сокрытие плавающего элемента */
        this._emit('hide');
    };
    OmnicaPopper.prototype._schedule = function (operation, delay) {
        clearTimeout(this._scheduleTimer);
        if (delay > 0) {
            this._scheduleTimer = setTimeout(function () { return operation(); }, delay);
        }
        else {
            operation();
        }
    };
    OmnicaPopper.prototype.changeTarget = function (target) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._target = target;
                        this._stopTargetListening();
                        this._startTargetListening();
                        if (!this._shown) return [3 /*break*/, 2];
                        this.autoUpdateOn();
                        return [4 /*yield*/, this.adjust()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    OmnicaPopper.prototype.changeTargetTriggers = function (triggers) {
        if (!isEqual(this._targetTriggers, triggers)) {
            this._targetTriggers = triggers;
            this._stopTargetListening();
            this._startTargetListening();
        }
    };
    OmnicaPopper.prototype.changePopperTriggers = function (triggers) {
        if (!isEqual(this._popperTriggers, triggers)) {
            this._popperTriggers = triggers;
            this._stopPopperListening();
            this._startPopperListening();
        }
    };
    OmnicaPopper.prototype.changeContainer = function (container) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._container = container;
                        if (!this._shown) return [3 /*break*/, 2];
                        this.autoUpdateOff();
                        this._teleport();
                        return [4 /*yield*/, this.adjust()];
                    case 1:
                        _a.sent();
                        this.autoUpdateOn();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    OmnicaPopper.prototype._startTargetListening = function () {
        var target = this.target();
        var triggers = this._targetTriggers;
        if (target) {
            this._enableShowTriggers(target, this._targetEvents, triggers);
            this._enableHideTriggers(target, this._targetEvents, triggers.length > 0 ? __spreadArray(__spreadArray([], triggers, true), ['click'], false) : []);
        }
    };
    OmnicaPopper.prototype._stopTargetListening = function () {
        this._targetEvents.clear();
    };
    OmnicaPopper.prototype._startPopperListening = function () {
        var triggers = this._popperTriggers;
        this._enableShowTriggers(this._el, this._popperEvents, triggers);
        this._enableHideTriggers(this._el, this._popperEvents, triggers);
    };
    OmnicaPopper.prototype._stopPopperListening = function () {
        this._popperEvents.clear();
    };
    OmnicaPopper.prototype._contains = function (el) {
        return el instanceof Element && this._el.contains(el);
    };
    OmnicaPopper.prototype.adjust = function () {
        return __awaiter(this, void 0, void 0, function () {
            var target;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        target = this._target();
                        if (!target || this._disposed) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, move(this._el, target, __assign(__assign({}, this._options), { onReferenceHidden: function () { return _this.hide(); } }))];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    OmnicaPopper.prototype.autoUpdateOff = function () {
        if (this._stopAutoUpdate) {
            this._stopAutoUpdate();
            this._stopAutoUpdate = null;
        }
    };
    OmnicaPopper.prototype.autoUpdateOn = function () {
        var _this = this;
        this.autoUpdateOff();
        var target = this._target();
        if (target) {
            this._stopAutoUpdate = autoUpdate(target, this._el, function () { return _this.adjust(); }, {
                elementResize: typeof ResizeObserver === 'function',
            });
        }
    };
    OmnicaPopper.prototype.detach = function () {
        var _a;
        (_a = this._el.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(this._el);
    };
    OmnicaPopper.prototype.dispose = function () {
        if (this._disposed) {
            return;
        }
        this._disposed = true;
        this._stopTargetListening();
        this._stopPopperListening();
        this.hide(true);
        this.detach();
        var target = this._target();
        if (target) {
            renameAttribute(target, 'data-original-title', 'title');
        }
        /** Удаление с формы плавающего элемента */
        this._emit('dispose');
    };
    OmnicaPopper.prototype._teleport = function () {
        var _a;
        if (this._disposed) {
            return;
        }
        var target = this._target();
        if (!target) {
            return;
        }
        var container = typeof this._container === 'string'
            ? document.querySelector(this._container)
            : this._container === null
                ? (_a = target.closest('.omnica-popper-container')) !== null && _a !== void 0 ? _a : document.body
                : this._container;
        if (!container) {
            throw new Error('No container for popper: ' + this._container);
        }
        container.appendChild(this._el);
    };
    OmnicaPopper.prototype.destroy = function () {
        this.autoUpdateOff();
        this._unsubscribe();
        this.dispose();
    };
    OmnicaPopper.prototype._enableShowTriggers = function (target, events, triggers) {
        var _this = this;
        triggers.forEach(function (t) {
            var eventType = OmnicaPopper.SHOW_EVENTS_MAP[t];
            if (eventType) {
                events.on([target], eventType, function (event) {
                    if (!_this._shown || _this._hiding) {
                        event.processedByOmnicaPopper = true;
                        !_this._touched && _this.show();
                    }
                });
            }
        });
    };
    OmnicaPopper.prototype._enableHideTriggers = function (target, events, triggers) {
        var _this = this;
        triggers.forEach(function (t) {
            var eventType = OmnicaPopper.HIDE_EVENTS_MAP[t];
            if (eventType) {
                events.on([target], eventType, function (event) {
                    if (!event.processedByOmnicaPopper) {
                        _this.hide();
                    }
                });
            }
        });
    };
    OmnicaPopper.prototype._onGlobalTap = function (event, touch) {
        if (touch === void 0) { touch = false; }
        return __awaiter(this, void 0, void 0, function () {
            var contains;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        contains = this._clicked || this._contains(event.target);
                        return [4 /*yield*/, nextAnimationFrame()];
                    case 1:
                        _a.sent();
                        if (event.omnicaPopperCloseAll || (event.omnicaPopperClose && contains) || (this.autoHide && !contains)) {
                            if (this._showing || !this._shown) {
                                return [2 /*return*/];
                            }
                            this.hide();
                            if (event.omnicaPopperClose) {
                                /** Сокрытие плавающего элемента по клику на элемент с директивой vPopperClose */
                                this._emit('hide:by-closer');
                            }
                            else {
                                this._emit('hide:by-miss-click');
                            }
                            if (touch) {
                                this._touched = true;
                                setTimeout(function () { return _this._touched = false; }, 300);
                            }
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    OmnicaPopper.SHOW_EVENTS_MAP = {
        hover: 'mouseenter',
        focus: 'focus',
        click: 'click',
        touch: 'touchstart',
    };
    OmnicaPopper.HIDE_EVENTS_MAP = {
        hover: 'mouseleave',
        focus: 'blur',
        click: 'click',
        touch: 'touchend',
    };
    return OmnicaPopper;
}());

export { PLACEMENT, addCloserListeners, OmnicaPopper as default, removeCloserListeners };
